// File Panel Component - provides file upload, management, and AI processing interface
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import CheckIcon from '@mui/icons-material/Check';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { copyImageUrlToClipboard } from '../utils/clipboardUtils';
import ImageIcon from '@mui/icons-material/Image';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import { fileStorageService } from '../services/FileStorageService';
import { fileContextService } from '../services/FileContextService';
import { downloadAssetsZip } from '../services/AssetBundleService';
import { documentProcessorService } from '../services/DocumentProcessorService';
import openAIService from '../services/OpenAIService';
import { alertService } from './Alert';
import logger from '../utils/logger';
import inputSanitizer from '../utils/inputSanitizer';
import { normalizeMediaUrl } from '../utils/mediaUrl';
import { isStageableAsset } from '../utils/assetStageNodes';
import SendToDeviceModal from './ui/SendToDeviceModal';
import ReceiveFromDeviceModal from './ui/ReceiveFromDeviceModal';
import BatchDownloadModal from './ui/BatchDownloadModal';
import { Lightbox } from './ui/Lightbox';
import { ThumbnailImage } from './ThumbnailImage';
import './FilePanel.css';

// Instant tooltip wrapper for action buttons
// @ts-expect-error TS6133 - Kept for future use
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _InstantTooltip: React.FC<{ title: string; children: React.ReactElement }> = ({ title, children }) => (
  <Tooltip title={title} enterDelay={0} leaveDelay={0} arrow placement="top">
    {children}
  </Tooltip>
);

// Type definitions
interface StoredFile {
  id: string;
  name: string;
  type: string;
  size: number;
  url?: string;
  /** Local object URL or data URL used for immediate optimistic preview before a persistent URL is available */
  previewUrl?: string;
  /** Indicates this record was optimistically added and awaiting final persisted metadata */
  _temp?: boolean;
  category?: string;
  uploadedAt?: string;
  metadata?: Record<string, any>;
  tags?: string[];
  /** Short preview of text file content for thumbnail display */
  previewText?: string;
}

interface FileContext {
  fileId: string;
  type: string;
  category: string;
  content: {
    name?: string;
    type?: string;
    size?: number;
    keyPoints?: string[];
    [key: string]: any;
  };
  summary: string;
  searchableContent: string;
  contextPrompt: string;
  processingMethod: string;
  error?: string;
  metadata?: Record<string, unknown>;
}

interface UploadProgress {
  file: string;
  progress: number;
  stage: 'uploading' | 'processing' | 'complete';
}

/**
 * One asset queued for bulk deletion, paired with the canvas nodes that
 * reference it. The node ids are resolved once, up front, for the whole
 * selection - so the user gets a single confirmation instead of N dialogs -
 * but they are kept per-asset so that a partial failure only cleans the stage
 * for the assets that were actually deleted.
 */
interface BulkDeleteTarget {
  file: StoredFile;
  fileUrl: string;
  nodeIds: string[];
}

interface BulkDeleteFailure {
  name: string;
  reason: string;
}

interface FilePanelProps {
  onFileContext?: (contexts: FileContext[]) => void;
  onAddToStage?: (file: StoredFile) => void;
  /** Place several assets at once (multi-selection "Place on canvas"), laid out without overlap */
  onAddManyToStage?: (files: StoredFile[]) => void;
  /** Called when an asset is about to be deleted - returns list of affected node IDs */
  onDeleteAsset?: (fileId: string, fileUrl: string) => string[];
  /** Called to remove asset from stage nodes after user confirms - removes pages or deletes nodes as appropriate */
  onRemoveNodesFromStage?: (nodeIds: string[], fileUrl: string) => void;
  /**
   * Batch variant of onRemoveNodesFromStage: cleans the stage for many assets in
   * a single pass. Used by bulk delete; falls back to onRemoveNodesFromStage
   * per url when the host does not provide it.
   */
  onRemoveAssetsFromStage?: (nodeIds: string[], fileUrls: string[]) => void;
  /** Called when user sets an asset as the flow thumbnail */
  onSetFlowThumbnail?: (thumbnailUrl: string) => void;
  /** Called when user clicks "View All Assets" button in import panel */
  onOpenAssetsPanel?: () => void;
  isVisible?: boolean;
  position?: 'left' | 'right';
  currentFlowId?: string | null;
  /** Embedded mode removes fixed positioning for use within other components */
  embedded?: boolean;
  /** Import-only mode renders just the import section (drop zone + phone button) */
  importOnly?: boolean;
}

type FilterType = 'all' | 'image' | 'video' | 'audio' | 'text' | 'document';
type SortOption = 'date-desc' | 'date-asc' | 'name-asc' | 'name-desc' | 'size-desc' | 'size-asc';
type AssetScope = 'global' | 'flow';
type ViewMode = 'list' | 'grid';

/**
 * Skeleton loading placeholder for asset grid items
 * Shows a shimmer animation while assets are being loaded
 */
const SkeletonAssetCard: React.FC<{ index?: number }> = ({ index = 0 }) => {
  const style = {
    '--skeleton-delay': `${index * 0.08}s`
  } as React.CSSProperties;

  return (
    <div className="asset-skeleton" style={style}>
      <div className="skeleton-thumbnail" />
    </div>
  );
};

/**
 * Generate a thumbnail URL for an image file
 * Uses the /api/media/thumbnail endpoint for optimized thumbnails
 * Default width increased to 400px for higher quality thumbnails in the new UI
 */
const getThumbnailUrl = (fileUrl: string | undefined, width: number = 400): string | undefined => {
  if (!fileUrl) return undefined;
  
  // Check if it's a proxy URL that we can convert to thumbnail
  if (fileUrl.includes('/api/media/proxy?key=')) {
    const keyMatch = fileUrl.match(/key=([^&]+)/);
    if (keyMatch) {
      return `/api/media/thumbnail?key=${keyMatch[1]}&width=${width}&quality=85`;
    }
  }
  
  // For other URLs (data URLs, external URLs), return as-is
  return fileUrl;
};

const FilePanel: React.FC<FilePanelProps> = ({ onFileContext, onAddToStage, onAddManyToStage, onDeleteAsset, onRemoveNodesFromStage, onRemoveAssetsFromStage, onSetFlowThumbnail, onOpenAssetsPanel, isVisible = true, position = 'right', currentFlowId, embedded = false, importOnly = false }) => {
  // State management
  const [assetScope, _setAssetScope] = useState<AssetScope>('flow');
  const [isLoadingScope, setIsLoadingScope] = useState<boolean>(true); // Start as loading
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false); // Track initial load
  const [files, setFiles] = useState<StoredFile[]>([]);
  const [contexts, setContexts] = useState<FileContext[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  // Files whose AI context is being analysed right now (lazy, on-demand analysis)
  const [analyzingFileIds, setAnalyzingFileIds] = useState<Set<string>>(new Set());
  // Files whose last analysis attempt failed — surfaced on the tile, not swallowed
  const [failedContextFileIds, setFailedContextFileIds] = useState<Set<string>>(new Set());
  // "Send to Prompt" now waits on the on-demand analysis, so the button says so
  const [isSendingToPrompt, setIsSendingToPrompt] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, UploadProgress>>({});
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [sortOption, setSortOption] = useState<SortOption>('date-desc');
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('filePanel.viewMode');
    return (saved === 'list' || saved === 'grid') ? saved : 'grid';
  });
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [renamingFileId, setRenamingFileId] = useState<string | null>(null);
  const [renamingValue, setRenamingValue] = useState<string>('');
  const [editingTagsFileId, setEditingTagsFileId] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState<string>('');
  const [lightboxFile, setLightboxFile] = useState<StoredFile | null>(null);
  const [lightboxContent, setLightboxContent] = useState<string>('');
  const [lightboxIndex, setLightboxIndex] = useState<number>(-1);
  const [hoveredImage, setHoveredImage] = useState<{ file: StoredFile; rect: DOMRect } | null>(null);
  const [sendToDeviceFile, setSendToDeviceFile] = useState<StoredFile | null>(null);
  const [showReceiveFromDevice, setShowReceiveFromDevice] = useState<boolean>(false);
  // Files handed to the "Save to Phone" QR modal - every media asset, or just
  // the selection. Snapshotted when the modal opens: the server session is
  // created once, so the list must not change under it.
  const [batchDownloadFiles, setBatchDownloadFiles] = useState<StoredFile[] | null>(null);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    file: StoredFile;
    x: number;
    y: number;
  } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  
  // Delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    file: StoredFile;
    affectedNodeIds: string[];
  } | null>(null);

  // Bulk delete confirmation - one dialog for the whole selection
  const [bulkDeleteConfirmation, setBulkDeleteConfirmation] = useState<{
    targets: BulkDeleteTarget[];
    affectedNodeIds: string[];
  } | null>(null);
  const [isBulkDeleting, setIsBulkDeleting] = useState<boolean>(false);
  const [isBundling, setIsBundling] = useState<boolean>(false);
  // Outcome of the last bulk action. Kept separate from `error` because
  // loadFiles() clears `error` on every successful refresh, which would wipe a
  // partial-failure report the moment we refresh the list after deleting.
  const [bulkNotice, setBulkNotice] = useState<string | null>(null);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Anchor index for shift-click range selection (index into the visible list)
  const selectionAnchorRef = useRef<number | null>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Ref to always call the latest uploadFiles (avoids stale closure in callbacks)
  const uploadFilesRef = useRef<(fileList: File[]) => Promise<void>>(null!);

  // Save viewMode preference to localStorage
  useEffect(() => {
    localStorage.setItem('filePanel.viewMode', viewMode);
  }, [viewMode]);

  // Close context menu when clicking outside, pressing Escape, or scrolling
  useEffect(() => {
    const closeMenu = () => setContextMenu(null);
    const handleClickOutside = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        closeMenu();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    
    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
      window.addEventListener('scroll', closeMenu, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('scroll', closeMenu, true);
      };
    }
    return undefined;
  }, [contextMenu]);

  // Handle right-click on asset
  const handleAssetContextMenu = useCallback((e: React.MouseEvent, file: StoredFile) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Context menu dimensions (approximate - 7 items @ ~36px each + padding + divider)
    const menuWidth = 200;
    const menuHeight = 320;
    
    // Calculate position, keeping menu within viewport
    let x = e.clientX;
    let y = e.clientY;
    
    // Prevent menu from going off the right edge
    if (x + menuWidth > window.innerWidth) {
      x = window.innerWidth - menuWidth - 8;
    }
    
    // Prevent menu from going off the bottom edge
    if (y + menuHeight > window.innerHeight) {
      y = window.innerHeight - menuHeight - 8;
    }
    
    // Prevent menu from going off the left edge
    if (x < 8) {
      x = 8;
    }
    
    // Prevent menu from going off the top edge
    if (y < 8) {
      y = 8;
    }
    
    setContextMenu({
      file,
      x,
      y,
    });
  }, []);

  // Load files from storage based on current scope
  const isLoadingRef = useRef(false);
  // When a reload is requested while one is already in-flight (e.g. phone uploads
  // arriving in rapid succession via ReceiveFromDeviceModal), we set this flag so
  // the in-flight load triggers a follow-up pass. Without this, the second call
  // is silently dropped and the late files never appear in the asset list.
  const reloadPendingRef = useRef(false);
  const loadFiles = useCallback(async (): Promise<void> => {
    console.log('[FilePanel] loadFiles called - assetScope:', assetScope, 'currentFlowId:', currentFlowId, 'isLoadingRef:', isLoadingRef.current);

    // Prevent multiple simultaneous loads using ref (not state, which can cause initial load to be skipped)
    if (isLoadingRef.current) {
      // Queue a follow-up load so we don't drop the request entirely.
      reloadPendingRef.current = true;
      console.log('[FilePanel] Already loading, queued a follow-up reload');
      return;
    }

    isLoadingRef.current = true;
    setIsLoadingScope(true);
    try {
      // Initialize storage service first
      await fileStorageService.init();

      let storedFiles: Awaited<ReturnType<typeof fileStorageService.listFiles>>;
      if (assetScope === 'flow') {
        if (currentFlowId) {
          storedFiles = await fileStorageService.listFlowFiles(currentFlowId);
          console.log('[FilePanel] Loaded flow files:', storedFiles.length, 'for flow:', currentFlowId);
        } else {
          // Flow tab selected but no flow saved yet - show empty
          storedFiles = [];
          console.log('[FilePanel] Flow tab but no flow ID - showing empty');
        }
      } else {
        storedFiles = await fileStorageService.listFiles();
        console.log('[FilePanel] Loaded library files:', storedFiles.length);
      }
      
      const storedContexts = await fileStorageService.listContexts();

      console.log('[FilePanel] Loaded contexts:', storedContexts.length);
      console.log('[FilePanel] Contexts details:', storedContexts.map(c => ({
        fileId: c.fileId,
        type: c.type,
        hasContent: !!c.content,
        contentType: typeof c.content,
        contentKeys: c.content ? Object.keys(c.content) : []
      })));

      // Normalize URLs to work across environments (local vs staging vs production)
      // This converts any absolute localhost URLs to relative URLs
      const normalizedFiles = storedFiles.map(file => {
        // Find context for this file to get preview text for text files
        const fileContext = storedContexts.find(c => c.fileId === file.id);
        let previewText: string | undefined;
        
        // Extract preview text from context for text files
        if (fileContext?.content?.displayContent) {
          const rawPreview = String(fileContext.content.displayContent).substring(0, 150).trim();
          previewText = rawPreview.length < 150 
            ? rawPreview 
            : rawPreview.substring(0, rawPreview.lastIndexOf(' ')) + '...';
        }
        
        return {
          ...file,
          url: normalizeMediaUrl(file.url),
          previewText,
        };
      });

      // Files from R2 already include signed URLs, no need to download them
      // Just log if any are missing URLs for debugging
      normalizedFiles.forEach(file => {
        if (!file.url) {
          logger.warn('[FilePanel] File missing URL:', file.name, file.id);
        }
      });

      // Deduplicate files to prevent showing same asset multiple times
      // This can happen due to race conditions between optimistic updates and storage refresh
      const seenIds = new Set<string>();
      const deduplicatedFiles = normalizedFiles.filter(file => {
        // Skip files without ID
        if (!file.id) return false;
        
        // Check for duplicate ID (unique R2 key)
        if (seenIds.has(file.id)) {
          console.log('[FilePanel] Filtered duplicate by ID:', file.name, file.id);
          return false;
        }
        seenIds.add(file.id);
        
        return true;
      });

      if (deduplicatedFiles.length < normalizedFiles.length) {
        console.log(`[FilePanel] Removed ${normalizedFiles.length - deduplicatedFiles.length} duplicate files`);
      }

      setFiles(deduplicatedFiles);
      setContexts(storedContexts);
      // Seed the on-demand analyser with everything already persisted so a consumer
      // never re-pays for a file that was analysed in an earlier session.
      fileContextService.primeCache(storedContexts);
      setError(null);
      setHasLoadedOnce(true);
    } catch (error) {
      logger.error('[FilePanel] Failed to load files:', error);
      setError('Failed to load files: ' + (error as Error).message);
      setHasLoadedOnce(true); // Still mark as loaded even on error
    } finally {
      isLoadingRef.current = false;
      setIsLoadingScope(false);
      // If a follow-up reload was requested while we were running, drain it now.
      if (reloadPendingRef.current) {
        reloadPendingRef.current = false;
        // Defer to next tick so React can flush state from this pass first.
        // Read through the ref so we always call the latest version of loadFiles.
        setTimeout(() => { void loadFilesRef.current?.(); }, 0);
      }
    }
  }, [assetScope, currentFlowId]);

  // Keep a ref to the latest loadFiles so the finally-block can re-invoke without
  // creating a stale closure when scope/flow changes mid-flight.
  const loadFilesRef = useRef(loadFiles);
  loadFilesRef.current = loadFiles;

  // Load existing files on mount, when scope changes, or when flowId changes
  useEffect(() => {
    loadFiles();
  }, [assetScope, currentFlowId]); // Load when scope or flow changes

  // Track on-demand context analyses so the asset tiles can show progress.
  // Analysis used to happen invisibly at upload; now it happens when a consumer asks
  // for it, which is a wait where there used to be none — so it has to be visible.
  useEffect(() => {
    const unsubscribe = fileContextService.subscribe(({ fileId, status }) => {
      setAnalyzingFileIds(prev => {
        const next = new Set(prev);
        if (status === 'analyzing') {
          next.add(fileId);
        } else {
          next.delete(fileId);
        }
        return next;
      });
      if (status === 'error') {
        setFailedContextFileIds(prev => new Set(prev).add(fileId));
      } else if (status === 'ready') {
        setFailedContextFileIds(prev => {
          if (!prev.has(fileId)) return prev;
          const next = new Set(prev);
          next.delete(fileId);
          return next;
        });
      }
    });
    return unsubscribe;
  }, []);

  // Listen for file storage updates from other components
  useEffect(() => {
    const handleStorageUpdate = () => {
      logger.debug('[FilePanel] File storage updated, reloading files');
      loadFiles();
    };

    window.addEventListener('fileStorageUpdated', handleStorageUpdate);

    return () => {
      window.removeEventListener('fileStorageUpdated', handleStorageUpdate);
    };
  }, [loadFiles]);

  // Drop selected ids that no longer exist in storage (deleted here or elsewhere)
  // so bulk actions can never act on a file that is already gone.
  useEffect(() => {
    setSelectedFiles(prev => {
      if (prev.size === 0) return prev;
      const liveIds = new Set(files.map(f => f.id));
      const next = new Set(Array.from(prev).filter(id => liveIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [files]);


  // Handle file selection
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > 0) {
      uploadFilesRef.current(selectedFiles);
    }
    // Reset input so selecting the same files again still triggers onChange
    if (event.target) event.target.value = '';
  }, []);

  // Handle drag and drop
  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();

    // Enforce flow context for uploads
    if (!currentFlowId) {
      alertService.warning('Please open a flow to upload files.');
      dropZoneRef.current?.classList.remove('drag-over');
      return;
    }

    const droppedFiles = Array.from(event.dataTransfer.files);
    if (droppedFiles.length > 0) {
      uploadFilesRef.current(droppedFiles);
    }

    dropZoneRef.current?.classList.remove('drag-over');
  }, [currentFlowId]);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.add('drag-over');
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
  }, []);

  // Upload and process files
  const uploadFiles = async (fileList: File[]): Promise<void> => {
    console.log('[FilePanel] uploadFiles called with', fileList.length, 'files:', fileList.map(f => f.name), 'flowId:', currentFlowId);

    // Guard: require a flow context for uploads
    if (!currentFlowId) {
      alertService.warning('Please open a flow to upload files.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Ensure storage is initialized
      await fileStorageService.init();

      for (const file of fileList) {
        console.log('[FilePanel] Processing file:', file.name, 'type:', file.type, 'size:', file.size);
        // Validate and sanitize file
        const sanitizedFileName = inputSanitizer.sanitizeFileName(file.name);
        const fileValidation = inputSanitizer.validateFileContent(file, sanitizedFileName);

        if (!fileValidation.valid) {
          alertService.error(`Invalid file ${file.name}: ${fileValidation.errors.join(', ')}`);
          continue;
        }

        const fileTypeValidation = inputSanitizer.validateFileType(sanitizedFileName);
        if (!fileTypeValidation.valid) {
          alertService.error(fileTypeValidation.error || 'Invalid file type');
          continue;
        }

        const fileId = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Create a local object URL for immediate feedback if image (or potentially any file)
        let previewUrl: string | undefined;
        if (file.type.startsWith('image/')) {
          try {
            previewUrl = URL.createObjectURL(file);
          } catch (e) {
            logger.warn('[FilePanel] Failed to create object URL preview:', e);
          }
        }

        // Optimistically insert file into list so user sees it immediately
        const optimisticFile: StoredFile = {
          id: fileId,
            name: sanitizedFileName,
            type: file.type,
            size: file.size,
            previewUrl,
            _temp: true,
            uploadedAt: new Date().toISOString()
        };
        setFiles(prev => [optimisticFile, ...prev]);

        // Update progress
        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { file: sanitizedFileName, progress: 0, stage: 'uploading' }
        }));

        // Store file with sanitized name (may take time)
        // Include scope and flowId for flow-scoped uploads
        let storedFile: StoredFile | null = null;
        try {
          const uploadOptions: Record<string, any> = { 
            fileId, 
            name: sanitizedFileName,
            scope: 'flow', // Enforce flow scope
            flowId: currentFlowId,
          };
          storedFile = await fileStorageService.uploadFile(file, uploadOptions) as StoredFile;
        } catch (uploadErr) {
          logger.error('[FilePanel] Upload failed for file', sanitizedFileName, uploadErr);
          setUploadProgress(prev => ({
            ...prev,
            [fileId]: { ...prev[fileId], progress: 100, stage: 'complete' }
          }));
          // Mark optimistic file as failed (could add error state UI in future)
          continue; // Skip processing step
        }

        // Merge stored file data into optimistic entry
        if (storedFile) {
          setFiles(prev => prev.map(f => f.id === fileId ? {
            ...f,
            ...storedFile,
            // Preserve previewUrl if final url missing
            previewUrl: f.previewUrl || storedFile?.url,
            _temp: false
          } : f));
        }

        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { ...prev[fileId], progress: 50, stage: 'processing' }
        }));

        // Check if this is a document file that needs special processing
        const isDocument = (
          file.type === 'text/plain' ||
          file.type === 'text/markdown' ||
          file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          file.type === 'text/html' ||
          file.name.endsWith('.txt') ||
          file.name.endsWith('.md') ||
          file.name.endsWith('.docx') ||
          file.name.endsWith('.html')
        );

        console.log('[FilePanel] File check:', {
          fileName: file.name,
          fileType: file.type,
          isDocument,
          hasStoredFile: !!storedFile,
          storedFileId: storedFile?.id
        });

        // Process document files to extract markdown content
        if (isDocument && storedFile) {
          try {
            console.log('[FilePanel] Processing document:', file.name, 'fileId:', storedFile.id);
            const processedDoc = await documentProcessorService.processDocument(file);
            console.log('[FilePanel] Document processed:', {
              contentLength: processedDoc.content.length,
              displayContentLength: processedDoc.displayContent.length,
              tokens: processedDoc.metadata.tokens,
              contentPreview: processedDoc.content.substring(0, 100)
            });
            
            // Store document metadata as context
            const docContext: FileContext = {
              fileId: storedFile.id,
              type: 'document',
              category: 'document',
              content: {
                markdown: processedDoc.content,
                displayContent: processedDoc.displayContent,
                sections: processedDoc.sections,
                ...processedDoc.metadata
              },
              summary: `${file.name} - ${processedDoc.metadata.tokens.toLocaleString()} tokens, ${processedDoc.metadata.readingTime} min read`,
              searchableContent: processedDoc.content,
              contextPrompt: `Document: ${file.name}\n\nContent preview:\n${processedDoc.displayContent.substring(0, 500)}...`,
              processingMethod: 'document_processor'
            };
            
            console.log('[FilePanel] Saving context for fileId:', storedFile.id);
            console.log('[FilePanel] Context to save:', {
              fileId: docContext.fileId,
              type: docContext.type,
              contentKeys: Object.keys(docContext.content || {}),
              hasMarkdown: !!docContext.content?.markdown,
              markdownLength: docContext.content?.markdown?.length
            });
            
            await fileStorageService.saveFileContext(storedFile.id, docContext);
            console.log('[FilePanel] Context saved successfully for:', file.name);
            
            // Extract a short preview for thumbnail display (first ~150 chars, trimmed to last word)
            const rawPreview = processedDoc.displayContent.substring(0, 150).trim();
            const previewText = rawPreview.length < 150 
              ? rawPreview 
              : rawPreview.substring(0, rawPreview.lastIndexOf(' ')) + '...';
            
            // Update the file with preview text
            setFiles(prev => prev.map(f => f.id === storedFile.id ? {
              ...f,
              previewText
            } : f));
            
            setUploadProgress(prev => ({
              ...prev,
              [fileId]: { ...prev[fileId], progress: 100, stage: 'complete' }
            }));
            
            // Remove progress after delay
            setTimeout(() => {
              setUploadProgress(prev => {
                const updated = { ...prev };
                delete updated[fileId];
                return updated;
              });
            }, 2000);
            
            // Skip AI processing for documents - we already have the content
            continue;
          } catch (docError) {
            logger.error('[FilePanel] Document processing failed:', docError);
            alertService.error((docError as Error).message);
            // Continue to AI processing fallback
          }
        }

        // NO AI processing here. Uploading a file used to fire an OpenAI Vision call
        // per image (and a summarisation call per long .csv/.json/.yaml) whether or
        // not anyone ever read the result — a billable call nobody asked for. The
        // analysis now happens on demand in `fileContextService.ensureContext()`, run
        // by the first consumer that actually needs the file's context.

        setUploadProgress(prev => ({
          ...prev,
          [fileId]: { ...prev[fileId], progress: 100, stage: 'complete' }
        }));

        // Remove progress after delay
        setTimeout(() => {
          setUploadProgress(prev => {
            const updated = { ...prev };
            delete updated[fileId];
            return updated;
          });
        }, 2000);
      }

      // After all uploads, refresh to ensure we have any metadata/contexts not already merged
      await loadFiles();

      // Notify other FilePanel instances (e.g. assets gallery) to refresh
      window.dispatchEvent(new CustomEvent('fileStorageUpdated'));

    } catch (error) {
      logger.error('[FilePanel] Upload failed:', error);
      setError('Upload failed: ' + (error as Error).message);
    } finally {
      setIsProcessing(false);
    }
  };
  // Keep ref in sync so callbacks always call the latest version
  uploadFilesRef.current = uploadFiles;

  // Request delete confirmation - checks for stage nodes using this asset
  const handleRequestDeleteFile = (fileId: string): void => {
    const file = files.find(f => f.id === fileId);
    if (!file) return;
    
    // Check if any nodes on stage use this asset
    const fileUrl = file.url || file.previewUrl || '';
    const affectedNodeIds = onDeleteAsset?.(fileId, fileUrl) || [];
    
    // Show confirmation dialog
    setDeleteConfirmation({ file, affectedNodeIds });
  };

  // Actually delete the file after confirmation
  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteConfirmation) return;
    
    const { file, affectedNodeIds } = deleteConfirmation;
    const fileUrl = file.url || file.previewUrl || '';
    
    try {
      // First remove asset from stage nodes (removes pages or deletes nodes as appropriate)
      if (affectedNodeIds.length > 0 && onRemoveNodesFromStage) {
        onRemoveNodesFromStage(affectedNodeIds, fileUrl);
      }
      
      // Then delete from storage
      await fileStorageService.init();
      await fileStorageService.deleteFile(file.id);
      fileContextService.clear(file.id);
      await loadFiles();
      setSelectedFiles(prev => {
        const updated = new Set(prev);
        updated.delete(file.id);
        return updated;
      });
      
      // Close confirmation dialog
      setDeleteConfirmation(null);
      
      // No success message - deletion confirmed by dialog disappearing
    } catch (error) {
      logger.error('[FilePanel] Delete failed:', error);
      setError('Delete failed: ' + (error as Error).message);
      setDeleteConfirmation(null);
    }
  };

  // Cancel delete
  const handleCancelDelete = (): void => {
    setDeleteConfirmation(null);
  };

  // Legacy direct delete (kept for internal use / undo system)
  // @ts-expect-error TS6133 - Kept for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleDeleteFile = async (fileId: string): Promise<void> => {
    try {
      await fileStorageService.init();
      await fileStorageService.deleteFile(fileId);
      await loadFiles();
      setSelectedFiles(prev => {
        const updated = new Set(prev);
        updated.delete(fileId);
        return updated;
      });
    } catch (error) {
      logger.error('[FilePanel] Delete failed:', error);
      setError('Delete failed: ' + (error as Error).message);
    }
  };

  // Promote a flow file to library (copy it to global scope)
  // @ts-expect-error TS6133 - Kept for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handlePromoteToGlobal = async (fileId: string): Promise<void> => {
    if (assetScope !== 'flow') {
      alertService.warning('Only flow files can be promoted to library');
      return;
    }

    try {
      await fileStorageService.init();
      const file = files.find(f => f.id === fileId);
      if (!file) return;

      // Fetch the file content
      const response = await fetch(file.url || file.previewUrl || '');
      const blob = await response.blob();
      const fileObj = new File([blob], file.name, { type: file.type });

      // Upload to library scope
      await fileStorageService.uploadFile(fileObj, {
        scope: 'global',
        folder: 'promoted-from-flow'
      });

      alertService.success(`${file.name} promoted to library`);
    } catch (error) {
      logger.error('[FilePanel] Promote to library failed:', error);
      alertService.error('Failed to promote to library: ' + (error as Error).message);
    }
  };

  // Start renaming a file
  const handleStartRename = (fileId: string, currentName: string): void => {
    setRenamingFileId(fileId);
    setRenamingValue(currentName);
  };

  // Finish renaming a file
  const handleFinishRename = async (fileId: string): Promise<void> => {
    if (!renamingValue.trim()) {
      setRenamingFileId(null);
      return;
    }

    try {
      await fileStorageService.init();
      const file = files.find(f => f.id === fileId);
      if (file) {
        // Update file metadata with new name
        const updatedMetadata = {
          ...file.metadata,
          originalName: file.name,
          customName: renamingValue.trim()
        };
        // For now, we'll just update in memory
        // You may want to add a rename method to fileStorageService
        setFiles(prev => prev.map(f => 
          f.id === fileId ? { ...f, name: renamingValue.trim(), metadata: updatedMetadata } : f
        ));
      }
      setRenamingFileId(null);
      setRenamingValue('');
    } catch (error) {
      logger.error('[FilePanel] Rename failed:', error);
      setError('Rename failed: ' + (error as Error).message);
    }
  };

  // Add tag to file
  const handleAddTag = async (fileId: string, tag: string): Promise<void> => {
    if (!tag.trim()) return;

    try {
      const file = files.find(f => f.id === fileId);
      if (file) {
        const existingTags = file.tags || [];
        if (existingTags.includes(tag.trim())) return; // Tag already exists
        
        const updatedTags = [...existingTags, tag.trim()];
        setFiles(prev => prev.map(f => 
          f.id === fileId ? { ...f, tags: updatedTags } : f
        ));
        
        // Save to metadata
        await fileStorageService.init();
        // You may want to add an updateMetadata method to fileStorageService
      }
    } catch (error) {
      logger.error('[FilePanel] Add tag failed:', error);
    }
  };

  // Remove tag from file
  const handleRemoveTag = async (fileId: string, tag: string): Promise<void> => {
    try {
      const file = files.find(f => f.id === fileId);
      if (file && file.tags) {
        const updatedTags = file.tags.filter(t => t !== tag);
        setFiles(prev => prev.map(f => 
          f.id === fileId ? { ...f, tags: updatedTags } : f
        ));
      }
    } catch (error) {
      logger.error('[FilePanel] Remove tag failed:', error);
    }
  };

  // Send selected contexts to parent.
  // The contexts are produced lazily: a file the user never sent anywhere was never
  // analysed, so this is where the (billable) analysis is actually paid for. The
  // "Analyzing…" badge on each asset comes from the fileContextService subscription.
  const handleSendToPrompt = async (): Promise<void> => {
    const selectedFileIds = Array.from(selectedFiles);
    logger.debug('[FilePanel] Selected file IDs:', selectedFileIds);

    if (selectedFileIds.length === 0 || !onFileContext) {
      logger.warn('[FilePanel] No files selected or no handler', {
        hasHandler: !!onFileContext,
        selectedFilesSize: selectedFiles.size
      });
      return;
    }

    const resolved: FileContext[] = [];
    const failed: string[] = [];

    setIsSendingToPrompt(true);
    try {
      for (const fileId of selectedFileIds) {
        const file = files.find(f => f.id === fileId);
        try {
          const context = await fileContextService.ensureContext({
            fileId,
            fileName: file?.name,
            fileType: file?.type
          });
          resolved.push(context as unknown as FileContext);
        } catch (error) {
          logger.error('[FilePanel] Context analysis failed for', fileId, error);
          failed.push(file?.name || fileId);
        }
      }
    } finally {
      setIsSendingToPrompt(false);
    }

    if (resolved.length > 0) {
      onFileContext(resolved);
      logger.debug('[FilePanel] Contexts sent successfully:', resolved.length);
    }

    // Failures are surfaced, never swallowed. The old code caught the error, saved a
    // "Image file: foo.png" placeholder that looked like a real analysis, and moved
    // on — so the user paid for a call and silently got nothing useful, forever.
    if (failed.length > 0) {
      const names = failed.join(', ');
      alertService.error(
        resolved.length > 0
          ? `Could not analyse ${names}. The other files were sent — select these again to retry.`
          : `Could not analyse ${names}. Nothing was sent — try again.`
      );
    }
  };

  // Filter and sort files based on search, type, and sort option
  const filteredFiles = files
    .filter(file => {
      const matchesSearch = !searchQuery ||
        file.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        file.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        file.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesType = filterType === 'all' ||
        (filterType === 'image' && file.type.startsWith('image/')) ||
        (filterType === 'video' && file.type.startsWith('video/')) ||
        (filterType === 'audio' && file.type.startsWith('audio/')) ||
        (filterType === 'text' && (file.type.startsWith('text/') || file.type === 'application/pdf')) ||
        (filterType === 'document' && (file.type.includes('document') || file.type.includes('pdf')));

      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      switch (sortOption) {
        case 'date-desc':
          return new Date(b.uploadedAt || 0).getTime() - new Date(a.uploadedAt || 0).getTime();
        case 'date-asc':
          return new Date(a.uploadedAt || 0).getTime() - new Date(b.uploadedAt || 0).getTime();
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'size-desc':
          return (b.size || 0) - (a.size || 0);
        case 'size-asc':
          return (a.size || 0) - (b.size || 0);
        default:
          return 0;
      }
    });

  // Get context for a file
  const getFileContext = (fileId: string): FileContext | undefined => {
    return contexts.find(ctx => ctx.fileId === fileId);
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get file type label
  const getFileTypeLabel = (type: string): string => {
    if (type.startsWith('image/')) return 'IMG';
    if (type.startsWith('video/')) return 'VIDEO';
    if (type.startsWith('audio/')) return 'AUDIO';
    if (type.startsWith('text/')) return 'TXT';
    if (type === 'application/pdf') return 'PDF';
    if (type.includes('document')) return 'DOC';
    return 'FILE';
  };

  // Handle single click on file for lightbox preview
  const handleFileClick = async (file: StoredFile, index: number): Promise<void> => {
    // Set the index for navigation
    setLightboxIndex(index);
    try {
      // For images, just open with the URL
      if (file.type.startsWith('image/')) {
        setLightboxFile(file);
        setLightboxContent('');
        return;
      }

      // For videos, open with the URL (video element will handle it)
      if (file.type.startsWith('video/')) {
        setLightboxFile(file);
        setLightboxContent('');
        return;
      }

      // For audio, open with the URL (audio element will handle it)
      if (file.type.startsWith('audio/')) {
        setLightboxFile(file);
        setLightboxContent('');
        return;
      }

      // For text-based files, load content
      if (file.type.startsWith('text/') || file.type.includes('markdown') || file.type === 'application/pdf') {
        await fileStorageService.init();
        const fileData = await fileStorageService.getFileData(file.id);
        
        // Read as text
        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target?.result as string;
          setLightboxFile(file);
          setLightboxContent(text || 'Unable to read file content');
        };
        reader.onerror = () => {
          setLightboxFile(file);
          setLightboxContent('Error reading file');
        };
        reader.readAsText(fileData);
        return;
      }

      // For other types, show basic info
      alertService.info(`Preview not available for ${file.type} files`);
    } catch (error) {
      logger.error('[FilePanel] Failed to load file for preview:', error);
      alertService.error('Failed to load file content');
    }
  };

  // ---------------------------------------------------------------------------
  // Selection
  // ---------------------------------------------------------------------------

  // The selected files, in panel order. Selection deliberately survives
  // filtering: a selected file that the current filter hides is still part of
  // the selection, so the count in the action bar always matches what the
  // actions operate on.
  const selectedFileList = files.filter(file => selectedFiles.has(file.id));
  const isBulkBusy = isBundling || isBulkDeleting;
  // Phone transfer covers images and videos, same as the all-media button.
  const isPhoneMedia = (f: StoredFile): boolean => f.type.startsWith('image/') || f.type.startsWith('video/');
  const selectedPhoneMedia = selectedFileList.filter(isPhoneMedia);
  const selectedStageable = selectedFileList.filter(isStageableAsset);

  const toggleFileSelection = (fileId: string, index: number): void => {
    selectionAnchorRef.current = index;
    setSelectedFiles(prev => {
      const updated = new Set(prev);
      if (updated.has(fileId)) {
        updated.delete(fileId);
      } else {
        updated.add(fileId);
      }
      return updated;
    });
  };

  // Shift-click extends the selection from the last toggled card to this one.
  const selectFileRange = (index: number): void => {
    const anchor = selectionAnchorRef.current;
    if (anchor === null || anchor >= filteredFiles.length) {
      const file = filteredFiles[index];
      if (file) toggleFileSelection(file.id, index);
      return;
    }
    const start = Math.min(anchor, index);
    const end = Math.max(anchor, index);
    const idsInRange = filteredFiles.slice(start, end + 1).map(f => f.id);
    setSelectedFiles(prev => new Set([...Array.from(prev), ...idsInRange]));
  };

  const selectAllVisible = (): void => {
    setSelectedFiles(new Set(filteredFiles.map(f => f.id)));
    selectionAnchorRef.current = null;
  };

  const clearSelection = (): void => {
    setSelectedFiles(new Set());
    selectionAnchorRef.current = null;
  };

  // Card click: modifier keys drive selection, a plain click still opens the
  // lightbox (existing behaviour).
  const handleCardClick = (e: React.MouseEvent, file: StoredFile, index: number): void => {
    if (e.shiftKey) {
      e.preventDefault();
      selectFileRange(index);
      return;
    }
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      toggleFileSelection(file.id, index);
      return;
    }
    void handleFileClick(file, index);
  };

  // ---------------------------------------------------------------------------
  // Bulk actions
  // ---------------------------------------------------------------------------

  /**
   * Download the selection as one zip. The zip is built and streamed by the
   * server (AssetBundleService owns the transport) - a hundreds-of-MB video
   * bundle must never be assembled in tab memory.
   */
  const handleDownloadSelected = async (): Promise<void> => {
    const targets = selectedFileList;
    if (targets.length === 0 || isBulkBusy) return;

    setIsBundling(true);
    setBulkNotice(null);
    try {
      await downloadAssetsZip(targets.map(f => f.id), {
        flowId: currentFlowId || undefined,
        filename: `knods-assets-${new Date().toISOString().slice(0, 10)}.zip`
      });
    } catch (err) {
      // The bundle endpoint rejects on size/count caps and the reason is the
      // whole point of the message - show it verbatim, not a generic failure.
      const reason = err instanceof Error ? err.message : String(err);
      logger.error('[FilePanel] Asset bundle download failed:', err);
      setBulkNotice(`Couldn't download the zip: ${reason}`);
    } finally {
      setIsBundling(false);
    }
  };

  /**
   * Resolve, in one pass over the whole selection, which canvas nodes reference
   * the selected assets, then raise a single confirmation. Never one dialog per
   * asset.
   */
  const handleRequestBulkDelete = (): void => {
    const targets: BulkDeleteTarget[] = selectedFileList.map(file => {
      const fileUrl = file.url || file.previewUrl || '';
      return { file, fileUrl, nodeIds: onDeleteAsset?.(file.id, fileUrl) || [] };
    });
    if (targets.length === 0) return;

    // A single node can reference several of the selected assets - count it once.
    const affectedNodeIds = Array.from(new Set(targets.flatMap(t => t.nodeIds)));
    setBulkDeleteConfirmation({ targets, affectedNodeIds });
  };

  const handleCancelBulkDelete = (): void => {
    if (isBulkDeleting) return;
    setBulkDeleteConfirmation(null);
  };

  /**
   * Delete every selected asset. Storage runs first, per asset, and the stage
   * is only cleaned for the assets that were really deleted - otherwise a
   * failed delete would still cost the user the node that points at it.
   * Whatever fails stays in the list and stays selected.
   */
  const handleConfirmBulkDelete = async (): Promise<void> => {
    if (!bulkDeleteConfirmation || isBulkDeleting) return;
    const { targets } = bulkDeleteConfirmation;

    setIsBulkDeleting(true);
    const deleted: BulkDeleteTarget[] = [];
    const failures: BulkDeleteFailure[] = [];

    try {
      await fileStorageService.init();

      for (const target of targets) {
        try {
          const ok = await fileStorageService.deleteFile(target.file.id);
          if (ok === false) {
            failures.push({ name: target.file.name, reason: 'storage reported the delete did not happen' });
          } else {
            fileContextService.clear(target.file.id);
            deleted.push(target);
          }
        } catch (err) {
          failures.push({
            name: target.file.name,
            reason: err instanceof Error ? err.message : String(err)
          });
        }
      }

      // Stage cleanup, once, for the assets that are actually gone.
      const deletedNodeIds = Array.from(new Set(deleted.flatMap(t => t.nodeIds)));
      if (deletedNodeIds.length > 0) {
        if (onRemoveAssetsFromStage) {
          onRemoveAssetsFromStage(deletedNodeIds, deleted.map(t => t.fileUrl));
        } else if (onRemoveNodesFromStage) {
          // Host without the batch callback: one pass per deleted asset, and
          // only for its own nodes, so a failed delete never strips a node.
          deleted.forEach(t => {
            if (t.nodeIds.length > 0) onRemoveNodesFromStage(t.nodeIds, t.fileUrl);
          });
        }
      }

      // Re-read storage so the list shows what survived, not what we hoped.
      await loadFiles();
      window.dispatchEvent(new CustomEvent('fileStorageUpdated'));

      // Keep the failures selected so the user can retry them directly.
      const deletedIds = new Set(deleted.map(t => t.file.id));
      setSelectedFiles(new Set(targets.filter(t => !deletedIds.has(t.file.id)).map(t => t.file.id)));
      selectionAnchorRef.current = null;

      if (failures.length > 0) {
        const shown = failures.slice(0, 3).map(f => `"${f.name}" (${f.reason})`).join(', ');
        const more = failures.length > 3 ? `, and ${failures.length - 3} more` : '';
        setBulkNotice(
          `Deleted ${deleted.length} of ${targets.length} assets. Failed: ${shown}${more}. ` +
          `The ones that failed are still here and still selected - try again.`
        );
      } else {
        setBulkNotice(null);
      }
    } catch (err) {
      logger.error('[FilePanel] Bulk delete failed:', err);
      setBulkNotice('Delete failed: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setIsBulkDeleting(false);
      setBulkDeleteConfirmation(null);
    }
  };

  // Get the current filtered/sorted files for navigation
  const getFilteredFiles = useCallback((): StoredFile[] => {
    let result = [...files];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(file => file.name.toLowerCase().includes(query));
    }
    
    // Apply type filter
    if (filterType !== 'all') {
      result = result.filter(file => {
        if (filterType === 'image') return file.type.startsWith('image/');
        if (filterType === 'video') return file.type.startsWith('video/');
        if (filterType === 'audio') return file.type.startsWith('audio/');
        if (filterType === 'text') return file.type.startsWith('text/') || file.type.includes('markdown');
        if (filterType === 'document') return file.type === 'application/pdf';
        return true;
      });
    }
    
    return result;
  }, [files, searchQuery, filterType]);

  // Navigate to previous file in lightbox
  const handleLightboxPrev = useCallback(() => {
    const filteredFiles = getFilteredFiles();
    if (lightboxIndex > 0) {
      const prevFile = filteredFiles[lightboxIndex - 1];
      handleFileClick(prevFile, lightboxIndex - 1);
    }
  }, [lightboxIndex, getFilteredFiles]);

  // Navigate to next file in lightbox
  const handleLightboxNext = useCallback(() => {
    const filteredFiles = getFilteredFiles();
    if (lightboxIndex < filteredFiles.length - 1) {
      const nextFile = filteredFiles[lightboxIndex + 1];
      handleFileClick(nextFile, lightboxIndex + 1);
    }
  }, [lightboxIndex, getFilteredFiles]);

  // Close lightbox
  const closeLightbox = useCallback(() => {
    setLightboxFile(null);
    setLightboxContent('');
    setLightboxIndex(-1);
  }, []);

  // Handle adding current lightbox file to stage
  const handleAddToStage = useCallback(() => {
    if (lightboxFile && onAddToStage) {
      onAddToStage(lightboxFile);
      closeLightbox();
    }
  }, [lightboxFile, onAddToStage, closeLightbox]);

  // Handle setting current lightbox image as flow thumbnail
  const handleSetAsThumbnail = useCallback(() => {
    if (lightboxFile && onSetFlowThumbnail && lightboxFile.type.startsWith('image/')) {
      const thumbnailUrl = lightboxFile.url || lightboxFile.previewUrl;
      if (thumbnailUrl) {
        onSetFlowThumbnail(thumbnailUrl);
      }
      closeLightbox();
    }
  }, [lightboxFile, onSetFlowThumbnail, closeLightbox]);

  // Handle manage tags from lightbox
  // @ts-expect-error TS6133 - Kept for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleLightboxManageTags = useCallback(() => {
    if (lightboxFile) {
      setEditingTagsFileId(lightboxFile.id);
      closeLightbox();
    }
  }, [lightboxFile, closeLightbox]);

  // Handle send to device from lightbox
  const handleLightboxSendToDevice = useCallback(() => {
    if (lightboxFile) {
      setSendToDeviceFile(lightboxFile);
      closeLightbox();
    }
  }, [lightboxFile, closeLightbox]);

  // Handle download from lightbox
  const handleLightboxDownload = useCallback(() => {
    if (lightboxFile) {
      const link = document.createElement('a');
      link.href = lightboxFile.url || lightboxFile.previewUrl || '';
      link.download = lightboxFile.name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [lightboxFile]);

  // Handle delete from lightbox
  const handleLightboxDelete = useCallback(() => {
    if (lightboxFile) {
      const fileId = lightboxFile.id;
      closeLightbox();
      // Use setTimeout to ensure lightbox closes before dialog opens
      setTimeout(() => {
        handleRequestDeleteFile(fileId);
      }, 0);
    }
  }, [lightboxFile, closeLightbox, handleRequestDeleteFile]);

  // For non-embedded mode, hide completely when not visible
  // For embedded mode, render but may be hidden by parent
  if (!isVisible && !embedded && !importOnly) return null;

  // Import-only mode: render just the import section
  if (importOnly) {
    return (
      <div className="import-panel">
        {/* Large Drop Zone */}
        <div
          ref={dropZoneRef}
          className="drop-zone-large"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <CloudUploadIcon />
          <span className="drop-zone-title">Drop files here</span>
          <span className="drop-zone-subtitle">or click to browse</span>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept="image/*,video/*,audio/*,.pdf,.txt,.md,.doc,.docx"
          />
        </div>

        {/* Divider */}
        <div className="import-divider">or</div>
        
        {/* Phone Upload Section */}
        <div className="phone-upload-section">
          <button
            className="import-phone-btn"
            onClick={() => setShowReceiveFromDevice(true)}
            disabled={isProcessing || (assetScope === 'flow' && !currentFlowId)}
            title="Upload photos from your phone"
          >
            <PhoneIphoneIcon fontSize="small" />
            <span>Upload from Phone</span>
          </button>
          <p className="phone-upload-description">
            Scan a QR code with your phone to take photos or select files and upload them directly to this flow.
          </p>
        </div>

        {/* Upload Progress */}
        {Object.keys(uploadProgress).length > 0 && (
          <div className="upload-progress">
            {Object.entries(uploadProgress).map(([fileId, progress]) => (
              <div key={fileId} className="progress-item">
                <div className="progress-info">
                  <span className="progress-filename">{progress.file}</span>
                  <span className="progress-stage">{progress.stage}</span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${progress.progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Uploaded Files Grid */}
        {files.length > 0 && (
          <div className="import-files-section">
            <div className="import-files-header">
              <span className="import-files-title">
                Uploaded ({files.length})
              </span>
            </div>
            <div className="import-files-grid">
              {files.map((file) => {
                const context = contexts.find(c => c.fileId === file.id);
                const isImage = file.type.startsWith('image/');
                const isVideo = file.type.startsWith('video/');
                const isDocument = file.type.includes('text') || file.type.includes('document');
                const isProcessing = file._temp || uploadProgress[file.id];

                const handleDragStart = (e: React.DragEvent) => {
                  const dragData = {
                    type: 'asset',
                    fileId: file.id,
                    fileName: file.name,
                    fileType: file.type,
                    isImage: isImage,
                    isVideo: isVideo,
                    isDocument: isDocument,
                    fileUrl: file.url || file.previewUrl,
                    previewUrl: file.previewUrl,
                    content: context?.content,
                    metadata: context?.metadata,
                    scope: assetScope,
                    context: context ? {
                      summary: context.summary,
                      content: context.content,
                      type: context.type
                    } : null
                  };
                  e.dataTransfer.setData('application/json', JSON.stringify(dragData));
                  e.dataTransfer.effectAllowed = 'copy';
                };

                return (
                  <div
                    key={file.id}
                    className={`import-file-item ${isProcessing ? 'processing' : ''}`}
                    draggable={!isProcessing}
                    onDragStart={handleDragStart}
                    style={{ cursor: isProcessing ? 'default' : 'grab' }}
                    title={isProcessing ? 'Processing...' : file.name}
                  >
                    <div className="import-item-preview">
                      {isImage ? (
                        (file.url || file.previewUrl) ? (
                          <div className="thumbnail-container">
                            <ThumbnailImage
                              src={getThumbnailUrl(file.url || file.previewUrl, 200)}
                              alt={file.name}
                              loading="lazy"
                              style={{ opacity: isProcessing ? 0.5 : 1 }}
                              onError={(e) => {
                                const img = e.target as HTMLImageElement;
                                img.classList.add('error');
                                const fallback = img.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                            <div className="thumbnail-fallback" style={{ display: 'none' }}>
                              <span className="file-type-badge">IMG</span>
                            </div>
                          </div>
                        ) : (
                          <div className="import-item-icon">
                            <span className="file-type-badge">IMG</span>
                          </div>
                        )
                      ) : isVideo ? (
                        <div className="thumbnail-container">
                          <ThumbnailImage
                            src={getThumbnailUrl(file.url || file.previewUrl, 200)}
                            alt={file.name}
                            loading="lazy"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: isProcessing ? 0.5 : 1 }}
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.classList.add('error');
                              const fallback = img.nextElementSibling as HTMLElement;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                          <div className="thumbnail-fallback" style={{ display: 'none' }}>
                            <span className="file-type-badge">VID</span>
                          </div>
                        </div>
                      ) : (
                        <div className="import-item-icon">
                          <span className="file-type-badge">
                            {isDocument ? 'DOC' : file.name.split('.').pop()?.toUpperCase().slice(0, 3) || 'FILE'}
                          </span>
                        </div>
                      )}
                      {isProcessing && (
                        <div className="import-item-processing-label">
                          Processing...
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* View All Assets Button */}
        {files.length > 0 && onAddToStage && (
          <div className="import-view-all-container">
            <button
              className="import-view-all-btn"
              onClick={() => {
                // Call callback to open assets panel
                if (onOpenAssetsPanel) {
                  onOpenAssetsPanel();
                }
              }}
              title="View all assets in the gallery"
            >
              <ImageIcon fontSize="small" />
              <span>View All Assets</span>
            </button>
          </div>
        )}

        {/* Receive from Device Modal */}
        {showReceiveFromDevice && (
          <ReceiveFromDeviceModal
            onClose={() => setShowReceiveFromDevice(false)}
            onFilesReceived={() => {
              // Reload files when new ones are received
              loadFiles();
            }}
            scope={assetScope}
            flowId={currentFlowId}
          />
        )}
      </div>
    );
  }

  return (
    <div ref={panelRef} className={`file-panel ${position} ${isCollapsed ? 'collapsed' : ''} ${embedded ? 'embedded' : ''}`}>
      {/* Header */}
      <div className="file-panel-header">
        <div className="file-panel-title">
          <span>Flow Assets</span>
          <div className="file-panel-stats">
            {isLoadingScope ? (
              <span className="loading-indicator">Loading assets...</span>
            ) : (
              <>
                {files.length} asset{files.length !== 1 ? 's' : ''}
                {selectedFiles.size > 0 && (
                  <span className="selected-count"> • {selectedFiles.size} selected</span>
                )}
              </>
            )}
          </div>
        </div>
        <button
          className="collapse-btn"
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? 'Expand panel' : 'Collapse panel'}
        >
          {isCollapsed ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </button>
      </div>

      {!isCollapsed && (
        <div className="file-panel-body">
          {/* Merged Upload Section (formerly the Import tab) */}
          <div className="assets-upload-section">
            <div
              ref={dropZoneRef}
              className="drop-zone-large"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUploadIcon />
              <span className="drop-zone-title">Drop files here</span>
              <span className="drop-zone-subtitle">or click to browse</span>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept="image/*,video/*,audio/*,.pdf,.txt,.md,.doc,.docx"
              />
            </div>
            <button
              className="import-phone-btn"
              onClick={() => setShowReceiveFromDevice(true)}
              disabled={isProcessing || (assetScope === 'flow' && !currentFlowId)}
              title="Upload photos from your phone"
            >
              <PhoneIphoneIcon fontSize="small" />
              <span>Upload from Phone</span>
            </button>
          </div>

          {/* Tabs - Hidden as we are enforcing flow-only scope */}
          {/* <div className="file-panel-tabs">
            <button
              className={`tab-btn ${assetScope === 'flow' ? 'active' : ''}`}
              onClick={() => setAssetScope('flow')}
              title="Files specific to this flow"
              disabled={isLoadingScope}
            >
              <FolderIcon fontSize="small" /> {isLoadingScope && assetScope !== 'flow' ? 'Loading...' : 'Flow'}
            </button>
            <button
              className={`tab-btn ${assetScope === 'global' ? 'active' : ''}`}
              onClick={() => setAssetScope('global')}
              title="Files accessible across all flows"
              disabled={isLoadingScope}
            >
              <FolderIcon fontSize="small" /> {isLoadingScope && assetScope !== 'global' ? 'Loading...' : 'Library'}
            </button>
          </div> */}

          {/* Search, Filter, Sort, and View Toggle */}
          <div className="file-panel-search">
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as FilterType)}
              className="filter-select"
              title="Filter by type"
            >
              <option value="all">All Types</option>
              <option value="image">Images</option>
              <option value="video">Videos</option>
              <option value="audio">Audio</option>
              <option value="text">Text</option>
              <option value="document">Documents</option>
            </select>
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value as SortOption)}
              className="sort-select"
              title="Sort by"
            >
              <option value="date-desc">Newest First</option>
              <option value="date-asc">Oldest First</option>
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
              <option value="size-desc">Largest First</option>
              <option value="size-asc">Smallest First</option>
            </select>
            <div className="view-toggle">
              <button
                className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List view"
              >
                <ViewListIcon fontSize="small" />
              </button>
              <button
                className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid view"
              >
                <ViewModuleIcon fontSize="small" />
              </button>
            </div>
          </div>

          {/* Selected Files Actions */}
          {selectedFiles.size > 0 && (
            <div className="file-panel-controls selection-bar">
              <span className="selection-count">{selectedFiles.size} selected</span>
              <button
                className="send-btn"
                onClick={handleSendToPrompt}
                disabled={isBulkBusy || isSendingToPrompt}
                title="Send selected files as context to prompt"
              >
                {isSendingToPrompt ? 'Analyzing…' : 'Send to Prompt'}
              </button>
              {onAddManyToStage && (
                <button
                  className="send-btn"
                  onClick={() => onAddManyToStage(selectedStageable)}
                  disabled={isBulkBusy || selectedStageable.length === 0}
                  title="Place the selected assets on the canvas, side by side"
                >
                  <AddToPhotosIcon style={{ fontSize: 14 }} />
                  Place on canvas
                </button>
              )}
              <button
                className="send-btn"
                onClick={handleDownloadSelected}
                disabled={isBulkBusy}
                title="Download the selected assets as a single zip"
              >
                <FolderZipIcon style={{ fontSize: 14 }} />
                {isBundling ? 'Preparing zip…' : 'Download zip'}
              </button>
              <button
                className="send-btn"
                onClick={() => setBatchDownloadFiles(selectedPhoneMedia)}
                disabled={isBulkBusy || selectedPhoneMedia.length === 0}
                title={selectedPhoneMedia.length === 0
                  ? 'Only images and videos can be sent to your phone'
                  : 'Generate a QR code to save the selected images and videos on your phone'}
              >
                <PhoneIphoneIcon style={{ fontSize: 14 }} />
                Send to phone
              </button>
              <button
                className="send-btn danger"
                onClick={handleRequestBulkDelete}
                disabled={isBulkBusy}
                title="Delete the selected assets"
              >
                <DeleteSweepIcon style={{ fontSize: 14 }} />
                {isBulkDeleting ? 'Deleting…' : 'Delete'}
              </button>
              <span className="selection-bar-spacer" />
              {selectedFiles.size < filteredFiles.length && (
                <button
                  className="selection-link-btn"
                  onClick={selectAllVisible}
                  disabled={isBulkBusy}
                  title="Select every asset currently listed"
                >
                  Select all ({filteredFiles.length})
                </button>
              )}
              <button
                className="selection-link-btn"
                onClick={clearSelection}
                disabled={isBulkBusy}
                title="Clear the selection"
              >
                Clear
              </button>
              {isBulkBusy && (
                <div
                  className="bulk-progress"
                  role="progressbar"
                  aria-label={isBundling ? 'Preparing zip' : 'Deleting assets'}
                >
                  <div className="bulk-progress-fill" />
                </div>
              )}
            </div>
          )}

          {/* Batch Download to Phone */}
          {files.filter(f => f.type.startsWith('image/') || f.type.startsWith('video/')).length > 0 && (
            <div className="file-panel-controls">
              <button
                className="send-btn secondary"
                onClick={() => setBatchDownloadFiles(files.filter(isPhoneMedia))}
                title="Generate QR code to download all media files on your phone"
              >
                <PhoneIphoneIcon fontSize="small" style={{ fontSize: 16 }} />
                Save to Phone ({files.filter(f => f.type.startsWith('image/') || f.type.startsWith('video/')).length})
              </button>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="error-message">
              <span className="error-icon">!</span>
              {error}
              <button onClick={() => setError(null)} className="error-close">×</button>
            </div>
          )}

          {/* Bulk action outcome (zip caps, partial delete failures) */}
          {bulkNotice && (
            <div className="error-message bulk-notice">
              <span className="error-icon">!</span>
              {bulkNotice}
              <button onClick={() => setBulkNotice(null)} className="error-close">×</button>
            </div>
          )}

          {/* Upload Progress */}
          {Object.keys(uploadProgress).length > 0 && (
            <div className="upload-progress">
              {Object.entries(uploadProgress).map(([fileId, progress]) => (
                <div key={fileId} className="progress-item">
                  <div className="progress-info">
                    <span className="progress-filename">{progress.file}</span>
                    <span className="progress-stage">{progress.stage}</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${progress.progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* File List */}
          <div className={`file-list ${viewMode === 'grid' ? 'grid-view' : 'list-view'}`}>
            {/* Loading skeletons */}
            {isLoadingScope && !hasLoadedOnce ? (
              <>
                {Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonAssetCard key={`skeleton-${i}`} index={i} />
                ))}
              </>
            ) : filteredFiles.length === 0 && hasLoadedOnce ? (
              <div className="empty-state-centered">
                {files.length === 0 ? (
                  <>
                    <div className="empty-icon">📁</div>
                    <p className="empty-title">No assets yet</p>
                    <p className="empty-subtitle">Drop files here or import from your phone</p>
                  </>
                ) : (
                  <>
                    <div className="empty-icon">🔍</div>
                    <p className="empty-title">No matches</p>
                    <p className="empty-subtitle">Try a different search term</p>
                  </>
                )}
              </div>
            ) : (
              filteredFiles.map((file, fileIndex) => {
                const context = getFileContext(file.id);
                const isSelected = selectedFiles.has(file.id);
                const isAnalyzing = analyzingFileIds.has(file.id);
                const analysisFailed = failedContextFileIds.has(file.id);

                const handleDragStart = (e: React.DragEvent) => {
                  const isImage = file.type.startsWith('image/');
                  const isVideo = file.type.startsWith('video/');
                  const isDocument = !isImage && !isVideo && (
                    file.type === 'text/plain' ||
                    file.type === 'text/markdown' ||
                    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                    file.type === 'text/html' ||
                    file.name.endsWith('.txt') ||
                    file.name.endsWith('.md') ||
                    file.name.endsWith('.docx') ||
                    file.name.endsWith('.html')
                  );
                  
                  console.log('[FilePanel] Dragging file:', file.name, 'fileId:', file.id, 'isVideo:', isVideo);
                  console.log('[FilePanel] Context found:', context);
                  console.log('[FilePanel] Context content:', context?.content);
                  
                  const dragData = {
                    fileId: file.id,
                    fileName: file.name,
                    fileType: file.type,
                    isImage: isImage,
                    isVideo: isVideo,
                    isDocument: isDocument,
                    fileUrl: file.url || file.previewUrl,
                    previewUrl: file.previewUrl,
                    content: context?.content,
                    metadata: context?.metadata,
                    scope: assetScope, // Include scope to identify if it's global or flow
                    context: context ? {
                      summary: context.summary,
                      content: context.content,
                      type: context.type
                    } : null
                  };
                  console.log('[FilePanel] Drag data being sent:', dragData);
                  e.dataTransfer.setData('application/json', JSON.stringify(dragData));
                  e.dataTransfer.effectAllowed = 'copy';
                };

                return viewMode === 'grid' ? (
                  // Grid View (Thumbnails)
                  <div
                    key={file.id}
                    className={`file-grid-item ${isSelected ? 'selected' : ''}`}
                    draggable="true"
                    onDragStart={handleDragStart}
                    onClick={(e) => handleCardClick(e, file, fileIndex)}
                    onContextMenu={(e) => handleAssetContextMenu(e, file)}
                    onMouseEnter={(e) => {
                      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setHoveredImage({ file, rect });
                      }
                    }}
                    onMouseLeave={() => setHoveredImage(null)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="grid-item-preview">
                      {file.type.startsWith('image/') ? (
                        (file.url || file.previewUrl) ? (
                          <div className="thumbnail-container">
                            <ThumbnailImage
                              src={getThumbnailUrl(file.url || file.previewUrl, 400)}
                              alt={file.name}
                              loading="lazy"
                              onError={(e) => {
                                // Show fallback on error
                                const img = e.target as HTMLImageElement;
                                img.classList.add('error');
                                const fallback = img.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                            <div className="thumbnail-fallback" style={{ display: 'none' }}>
                              <span className="file-type-badge">IMG</span>
                            </div>
                          </div>
                        ) : (
                          <div className="grid-item-icon">
                            <span className="file-type-badge">IMG</span>
                          </div>
                        )
                      ) : file.type.startsWith('video/') ? (
                        <div className="video-thumbnail-wrapper">
                          <div className="thumbnail-container">
                            <ThumbnailImage
                              src={getThumbnailUrl(file.url || file.previewUrl, 400)}
                              alt={file.name}
                              loading="lazy"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={(e) => {
                                const img = e.target as HTMLImageElement;
                                img.classList.add('error');
                                const fallback = img.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                            <div className="thumbnail-fallback" style={{ display: 'none' }}>
                              <span className="file-type-badge">VID</span>
                            </div>
                          </div>
                          <div className="video-overlay">
                            <span className="play-icon">▶</span>
                          </div>
                        </div>
                      ) : file.type.startsWith('audio/') ? (
                        <div className="audio-thumbnail-wrapper">
                          <div className="grid-item-icon audio-icon">
                            <AudiotrackIcon className="audio-glyph" />
                            <span className="file-type-badge">AUDIO</span>
                          </div>
                          {(file.url || file.previewUrl) && (
                            <audio
                              className="grid-audio-player"
                              src={file.url || file.previewUrl}
                              controls
                              preload="none"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </div>
                      ) : (
                        <div className="grid-item-icon">
                          <span className="file-type-badge">{getFileTypeLabel(file.type)}</span>
                          {file.previewText && (
                            <div className="text-preview-snippet">
                              {file.previewText}
                            </div>
                          )}
                        </div>
                      )}
                      {isAnalyzing && (
                        <div className="asset-analyzing-badge" role="status">
                          <span className="asset-analyzing-spinner" />
                          Analyzing…
                        </div>
                      )}
                      {!isAnalyzing && analysisFailed && (
                        <div className="asset-analysis-failed-badge" role="status">
                          Analysis failed
                        </div>
                      )}
                    </div>

                    {/* Selection toggle - shown on hover, or always once selected */}
                    <button
                      type="button"
                      className={`asset-select-toggle ${isSelected ? 'checked' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFileSelection(file.id, fileIndex);
                      }}
                      aria-pressed={isSelected}
                      title={isSelected ? 'Deselect (ctrl+click the card, shift+click for a range)' : 'Select (ctrl+click the card, shift+click for a range)'}
                    >
                      <CheckIcon />
                    </button>

                    {/* Hover overlay with action icons */}
                    <div className="asset-hover-overlay">
                      <div className="asset-hover-actions">
                        <button
                          className="asset-hover-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSendToDeviceFile(file);
                          }}
                          title="Send to device"
                        >
                          <PhoneIphoneIcon fontSize="small" />
                        </button>
                        <button
                          className="asset-hover-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            const link = document.createElement('a');
                            link.href = file.url || file.previewUrl || '';
                            link.download = file.name;
                            link.target = '_blank';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          title="Download"
                        >
                          <DownloadIcon fontSize="small" />
                        </button>
                        {file.type.startsWith('image/') && (
                          <button
                            className="asset-hover-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              const fullUrl = file.url || file.previewUrl || '';
                              if (!fullUrl) return;
                              copyImageUrlToClipboard(fullUrl)
                                .catch(err => console.error('Failed to copy image:', err));
                            }}
                            title="Copy to clipboard"
                          >
                            <ContentPasteIcon fontSize="small" />
                          </button>
                        )}
                        {onAddToStage && (
                          <button
                            className="asset-hover-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddToStage(file);
                            }}
                            title="Place on stage"
                          >
                            <AddToPhotosIcon fontSize="small" />
                          </button>
                        )}
                        {onSetFlowThumbnail && file.type.startsWith('image/') && (
                          <button
                            className="asset-hover-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              const thumbnailUrl = file.url || file.previewUrl;
                              if (thumbnailUrl) {
                                onSetFlowThumbnail(thumbnailUrl);
                              }
                            }}
                            title="Set as knod thumbnail"
                          >
                            <ImageIcon fontSize="small" />
                          </button>
                        )}
                        <button
                          className="asset-hover-icon danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRequestDeleteFile(file.id);
                          }}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Grid item actions - Select button hidden */}
                  </div>
                ) : (
                  // List View (Compact)
                  <div
                    key={file.id}
                    className={`file-item ${isSelected ? 'selected' : ''}`}
                    draggable="true"
                    onDragStart={handleDragStart}
                    onClick={(e) => handleCardClick(e, file, fileIndex)}
                    onContextMenu={(e) => handleAssetContextMenu(e, file)}
                    onMouseEnter={(e) => {
                      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setHoveredImage({ file, rect });
                      }
                    }}
                    onMouseLeave={() => setHoveredImage(null)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="file-item-header">
                      <div className="file-info">
                        <button
                          type="button"
                          className={`asset-select-toggle ${isSelected ? 'checked' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFileSelection(file.id, fileIndex);
                          }}
                          aria-pressed={isSelected}
                          title={isSelected ? 'Deselect (shift+click a row for a range)' : 'Select (shift+click a row for a range)'}
                        >
                          <CheckIcon />
                        </button>
                        {file.type.startsWith('image/') && (file.url || file.previewUrl) && (
                          <div className="file-thumbnail">
                            <ThumbnailImage src={getThumbnailUrl(file.url || file.previewUrl, 100)} alt={file.name} loading="lazy" />
                          </div>
                        )}
                        {file.type.startsWith('video/') && (file.url || file.previewUrl) && (
                          <div className="file-thumbnail video-thumbnail">
                            <ThumbnailImage
                              src={getThumbnailUrl(file.url || file.previewUrl, 100)}
                              alt={file.name}
                              loading="lazy"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={(e) => {
                                // Fallback: hide image if thumbnail generation fails
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                            <div className="video-overlay-small">
                              <span className="play-icon-small">▶</span>
                            </div>
                          </div>
                        )}
                        {file.type.startsWith('audio/') && (
                          <div className="file-thumbnail audio-thumbnail">
                            <AudiotrackIcon fontSize="small" />
                          </div>
                        )}
                        <div className="file-details">
                          {renamingFileId === file.id ? (
                            <input
                              type="text"
                              className="file-rename-input"
                              value={renamingValue}
                              onChange={(e) => setRenamingValue(e.target.value)}
                              onBlur={() => handleFinishRename(file.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleFinishRename(file.id);
                                if (e.key === 'Escape') setRenamingFileId(null);
                                e.stopPropagation();
                              }}
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                            />
                          ) : (
                            <div className="file-name-row">
                              <span className="file-type-label">{getFileTypeLabel(file.type)}</span>
                              <div 
                                className="file-name" 
                                title={file.name}
                                onDoubleClick={() => handleStartRename(file.id, file.name)}
                              >
                                {file.name}
                              </div>
                              <div className="file-meta">
                                {formatFileSize(file.size)}
                                {isAnalyzing && (
                                  <span className="asset-analyzing-inline" role="status"> · Analyzing…</span>
                                )}
                                {!isAnalyzing && analysisFailed && (
                                  <span className="asset-analysis-failed-inline" role="status"> · Analysis failed</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* File actions - Promote to Library and Select buttons hidden */}
                    </div>

                    {/* Tags section */}
                    {(file.tags && file.tags.length > 0) || editingTagsFileId === file.id ? (
                      <div className="file-tags">
                        {file.tags?.map((tag, idx) => (
                          <span key={idx} className="file-tag">
                            {tag}
                            <button
                              className="tag-remove"
                              onClick={() => handleRemoveTag(file.id, tag)}
                              title="Remove tag"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        {editingTagsFileId === file.id && (
                          <input
                            type="text"
                            className="tag-input"
                            placeholder="Add tag..."
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && tagInput.trim()) {
                                handleAddTag(file.id, tagInput);
                                setTagInput('');
                                e.stopPropagation();
                              }
                              if (e.key === 'Escape') {
                                setEditingTagsFileId(null);
                                setTagInput('');
                              }
                            }}
                            onBlur={() => {
                              setEditingTagsFileId(null);
                              setTagInput('');
                            }}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>

          {/* Panel Footer */}
          {files.length > 0 && (
            <div className="file-panel-footer">
              <div className="storage-info">
                Storage: {files.length} files
                {!openAIService.isConfigured() && (
                  <div className="ai-warning">
                    Configure OpenAI API for AI processing
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lightbox for File Preview */}
      {lightboxFile && (
        <Lightbox
          imageUrl={lightboxFile.url || lightboxFile.previewUrl || ''}
          onClose={closeLightbox}
          contentType={
            lightboxFile.type.startsWith('image/') ? 'image' :
            lightboxFile.type.startsWith('video/') ? 'video' :
            lightboxFile.type.startsWith('audio/') ? 'audio' :
            'text'
          }
          content={lightboxContent}
          title={lightboxFile.name}
          onPrev={handleLightboxPrev}
          onNext={handleLightboxNext}
          hasPrev={lightboxIndex > 0}
          hasNext={lightboxIndex < getFilteredFiles().length - 1}
          onAddToStage={onAddToStage ? handleAddToStage : undefined}
          onSetAsThumbnail={onSetFlowThumbnail ? handleSetAsThumbnail : undefined}
          showThumbnailButton={lightboxFile.type.startsWith('image/')}
          onSendToDevice={handleLightboxSendToDevice}
          onDownload={handleLightboxDownload}
          onDelete={handleLightboxDelete}
        />
      )}

      {/* Image/Video Hover Tooltip */}
      {hoveredImage && ReactDOM.createPortal(
        <div
          style={(() => {
            const maxSize = 400;
            const gap = 10;
            const { rect } = hoveredImage;
            
            // Get the panel's right edge to position preview outside the panel
            const panelRect = panelRef.current?.getBoundingClientRect();
            const panelRight = panelRect ? panelRect.right : rect.right;
            
            // Position to the right of the entire panel (not just the item)
            let left = panelRight + gap;
            // Vertically center on the hovered item
            let top = rect.top + (rect.height / 2);
            
            // Keep within viewport bounds horizontally
            if (left + maxSize > window.innerWidth - gap) {
              // Fall back to left of panel if no room on right
              const panelLeft = panelRect ? panelRect.left : rect.left;
              left = panelLeft - maxSize - gap;
            }
            if (left < gap) {
              left = gap;
            }
            
            // Keep within viewport bounds vertically
            // Since we use translateY(-50%), the actual top will be top - height/2
            // Clamp so the preview stays within viewport
            const halfMax = maxSize / 2;
            if (top - halfMax < gap) {
              top = halfMax + gap;
            }
            if (top + halfMax > window.innerHeight - gap) {
              top = window.innerHeight - halfMax - gap;
            }
            
            return {
              position: 'fixed' as const,
              left: `${left}px`,
              top: `${top}px`,
              transform: 'translateY(-50%)',
              maxWidth: `${maxSize}px`,
              maxHeight: `${maxSize}px`,
              pointerEvents: 'none' as const,
              zIndex: 9999,
              background: 'rgba(0, 0, 0, 0.9)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              padding: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
            };
          })()}
        >
          {hoveredImage.file.type.startsWith('video/') ? (
            <video
              src={hoveredImage.file.url || hoveredImage.file.previewUrl}
              autoPlay
              muted
              loop
              playsInline
              style={{
                display: 'block',
                maxWidth: '384px',
                maxHeight: '384px',
                width: 'auto',
                height: 'auto',
                borderRadius: '4px'
              }}
            />
          ) : (
            <img
              src={hoveredImage.file.url || hoveredImage.file.previewUrl}
              alt={hoveredImage.file.name}
              style={{
                display: 'block',
                maxWidth: '384px',
                maxHeight: '384px',
                width: 'auto',
                height: 'auto',
                borderRadius: '4px'
              }}
            />
          )}
        </div>,
        document.body
      )}

      {/* Send to Device Modal */}
      {sendToDeviceFile && (
        <SendToDeviceModal
          asset={{
            id: sendToDeviceFile.id,
            name: sendToDeviceFile.name,
            type: sendToDeviceFile.type,
            size: sendToDeviceFile.size,
            url: sendToDeviceFile.url || sendToDeviceFile.previewUrl || '',
            previewUrl: sendToDeviceFile.previewUrl
          }}
          onClose={() => setSendToDeviceFile(null)}
        />
      )}

      {/* Receive from Device Modal */}
      {showReceiveFromDevice && (
        <ReceiveFromDeviceModal
          onClose={() => setShowReceiveFromDevice(false)}
          onFilesReceived={() => {
            // Reload files when new ones are received
            loadFiles();
          }}
          scope={assetScope}
          flowId={currentFlowId}
        />
      )}

      {/* Batch Download Modal */}
      {batchDownloadFiles && (
        <BatchDownloadModal
          files={batchDownloadFiles
            .map(f => ({
              name: f.name,
              url: f.url || f.previewUrl || '',
              type: f.type,
              size: f.size,
            }))}
          onClose={() => setBatchDownloadFiles(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmation && ReactDOM.createPortal(
        <div className="delete-confirmation-overlay" onClick={handleCancelDelete} onMouseDown={(e) => e.stopPropagation()}>
          <div className="delete-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirmation-header">
              <span className="delete-icon">🗑️</span>
              <h3>Delete Asset?</h3>
            </div>
            <div className="delete-confirmation-body">
              <p className="delete-filename">"{deleteConfirmation.file.name}"</p>
              <p className="delete-warning">
                This will permanently delete the file from your assets.
              </p>
              {deleteConfirmation.affectedNodeIds.length > 0 && (
                <p className="delete-stage-warning">
                  ⚠️ This asset is used by <strong>{deleteConfirmation.affectedNodeIds.length} node{deleteConfirmation.affectedNodeIds.length > 1 ? 's' : ''}</strong> on the stage. 
                  It will be removed from {deleteConfirmation.affectedNodeIds.length > 1 ? 'those nodes' : 'that node'}. Nodes with no remaining content will be deleted.
                </p>
              )}
              <p className="delete-undo-note">
                This action cannot be undone yet. (Ctrl+Z support coming soon)
              </p>
            </div>
            <div className="delete-confirmation-actions">
              <button className="delete-cancel-btn" onClick={handleCancelDelete}>
                Cancel
              </button>
              <button className="delete-confirm-btn" onClick={handleConfirmDelete}>
                Delete{deleteConfirmation.affectedNodeIds.length > 0 ? ' & Remove from Stage' : ''}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Bulk Delete Confirmation Dialog - one dialog for the whole selection */}
      {bulkDeleteConfirmation && ReactDOM.createPortal(
        (() => {
          const { targets, affectedNodeIds } = bulkDeleteConfirmation;
          const assetCount = targets.length;
          const nodeCount = affectedNodeIds.length;
          const preview = targets.slice(0, 5);
          const remaining = assetCount - preview.length;

          return (
            <div className="delete-confirmation-overlay" onClick={handleCancelBulkDelete} onMouseDown={(e) => e.stopPropagation()}>
              <div className="delete-confirmation-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="delete-confirmation-header">
                  <span className="delete-icon">🗑️</span>
                  <h3>Delete {assetCount} asset{assetCount === 1 ? '' : 's'}?</h3>
                </div>
                <div className="delete-confirmation-body">
                  <p className="delete-warning">
                    This will permanently delete {assetCount} file{assetCount === 1 ? '' : 's'} from your assets.
                  </p>
                  <ul className="delete-file-list">
                    {preview.map(t => (
                      <li key={t.file.id} title={t.file.name}>{t.file.name}</li>
                    ))}
                    {remaining > 0 && (
                      <li className="delete-file-more">…and {remaining} more</li>
                    )}
                  </ul>
                  {nodeCount > 0 && (
                    <p className="delete-stage-warning">
                      ⚠️ <strong>{nodeCount} node{nodeCount === 1 ? '' : 's'}</strong> on the stage
                      {nodeCount === 1 ? ' uses' : ' use'} {assetCount === 1 ? 'this asset' : 'these assets'}.
                      {assetCount === 1 ? ' It' : ' They'} will be removed from {nodeCount === 1 ? 'that node' : 'those nodes'},
                      and nodes left with no remaining content will be deleted.
                    </p>
                  )}
                  <p className="delete-undo-note">
                    This action cannot be undone yet. (Ctrl+Z support coming soon)
                  </p>
                </div>
                <div className="delete-confirmation-actions">
                  <button className="delete-cancel-btn" onClick={handleCancelBulkDelete} disabled={isBulkDeleting}>
                    Cancel
                  </button>
                  <button className="delete-confirm-btn" onClick={handleConfirmBulkDelete} disabled={isBulkDeleting}>
                    {isBulkDeleting
                      ? `Deleting ${assetCount}…`
                      : `Delete ${assetCount}${nodeCount > 0 ? ' & Remove from Stage' : ''}`}
                  </button>
                </div>
              </div>
            </div>
          );
        })(),
        document.body
      )}

      {/* Asset Context Menu */}
      {contextMenu && ReactDOM.createPortal(
        <div
          ref={contextMenuRef}
          className="asset-context-menu"
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 10000,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {viewMode === 'list' && (
            <button
              className="context-menu-item"
              onClick={() => {
                handleStartRename(contextMenu.file.id, contextMenu.file.name);
                setContextMenu(null);
              }}
            >
              <EditIcon fontSize="small" />
              <span>Rename</span>
            </button>
          )}
          <button
            className="context-menu-item"
            onClick={() => {
              setSendToDeviceFile(contextMenu.file);
              setContextMenu(null);
            }}
          >
            <PhoneIphoneIcon fontSize="small" />
            <span>Send to device</span>
          </button>
          <button
            className="context-menu-item"
            onClick={() => {
              // Download the file
              const link = document.createElement('a');
              link.href = contextMenu.file.url || contextMenu.file.previewUrl || '';
              link.download = contextMenu.file.name;
              link.target = '_blank';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              setContextMenu(null);
            }}
          >
            <DownloadIcon fontSize="small" />
            <span>Download</span>
          </button>
          {onAddToStage && (
            <button
              className="context-menu-item"
              onClick={() => {
                onAddToStage(contextMenu.file);
                setContextMenu(null);
              }}
            >
              <AddToPhotosIcon fontSize="small" />
              <span>Place on stage</span>
            </button>
          )}
          {onSetFlowThumbnail && contextMenu.file.type.startsWith('image/') && (
            <button
              className="context-menu-item"
              onClick={() => {
                const thumbnailUrl = contextMenu.file.url || contextMenu.file.previewUrl;
                if (thumbnailUrl) {
                  onSetFlowThumbnail(thumbnailUrl);
                }
                setContextMenu(null);
              }}
            >
              <ImageIcon fontSize="small" />
              <span>Set as knod thumbnail</span>
            </button>
          )}
          <div className="context-menu-divider" />
          <button
            className="context-menu-item danger"
            onClick={() => {
              const fileId = contextMenu.file.id;
              setContextMenu(null);
              // Use setTimeout to ensure context menu closes before dialog opens
              setTimeout(() => {
                handleRequestDeleteFile(fileId);
              }, 0);
            }}
          >
            <DeleteIcon fontSize="small" />
            <span>Delete</span>
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

export default FilePanel;
